import { tabClasses, type Components } from '@mui/material';
import { CSSProperties } from 'react';
import { themeVars } from '../themeVars';
import { typographyStyles, type Theme } from './utils';

export const overlay: CSSProperties = {
  content: '""',
  position: 'absolute',
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  left: 0,
  bottom: 0,
};

export const verticalIndicatorSize = '4px';

export const MuiTabs: Components<Theme>['MuiTabs'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      minHeight: 40,
      [`& .${tabClasses.root}:focus-visible`]: {
        outline: `1px solid ${themeVars(theme).palette.border.focused}`,
      },
    }),
    list: ({ theme }) => ({
      gap: theme.spacing(0.5),
    }),
    indicator: {
      display: 'none',
    },
  },
  variants: [
    {
      props: ({ ownerState }) => ownerState.indicator !== 'vertical',
      style: ({ theme }) => ({
        [`& .${tabClasses.root}`]: {
          padding: theme.spacing(1, 2),
          minHeight: 0,

          [`&:before`]: {
            ...overlay,
            borderBlockEnd: `2px solid ${themeVars(theme).palette.border.subtle}`,
            transition: theme.transitions.create(['border-color'], {
              duration: theme.transitions.duration.short,
            }),
          },
          [`&:hover:before`]: {
            borderColor: themeVars(theme).palette.border.strong,
          },
          [`&.${tabClasses.selected}:before`]: {
            borderColor: themeVars(theme).palette.border.focused,
          },
        },
      }),
    },
    {
      props: ({ ownerState }) => ownerState.indicator === 'vertical',
      style: ({ theme }) => ({
        [`& .${tabClasses.root}`]: {
          paddingLeft: theme.spacing(2.5),
          paddingBlock: theme.spacing(1),
          minHeight: 40,
          // Minimum width could cause inconsistent spacing between indicator and text
          minWidth: 'auto',
          [`&:before`]: {
            ...overlay,
            left: theme.spacing(1),
            bottom: '50%',
            // Prevent unwanted horizontal overflow (horizontal scrollbars)
            width: 0,
            height: '1.2em',
            transform: 'translateY(50%)',
            border: 0,
            borderLeft: `0px solid transparent`,
            opacity: 0,
            transition: theme.transitions.create(['opacity', 'border-width', 'border-color'], {
              duration: theme.transitions.duration.standard,
            }),
          },
          '&:hover:before': {
            borderColor: themeVars(theme).palette.border.strong,
            borderWidth: verticalIndicatorSize,
            opacity: 1,
          },
          [`&.${tabClasses.selected}:before`]: {
            borderColor: themeVars(theme).palette.border.focused,
            borderWidth: verticalIndicatorSize,
            opacity: 1,
          },
        },
      }),
    },
    {
      props: ({ ownerState }) => ownerState.orientation === 'vertical',
      style: ({ theme }) => ({
        [`& .${tabClasses.root}`]: {
          alignItems: 'flex-start',
          padding: 0,
          paddingBlock: theme.spacing(1),
          paddingLeft: theme.spacing(3.5),
          minHeight: 0,
        },
      }),
    },
  ],
};

export const MuiTab: Components<Theme>['MuiTab'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      ...typographyStyles(theme.typography.body2),
      color: themeVars(theme).palette.text.secondary,
      opacity: 1,
      [`&:hover, &.${tabClasses.selected}`]: {
        color: themeVars(theme).palette.text.primary,
      },
    }),
  },
};
